import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Callout — the tinted advisory band set into the flow of a page: an
 * explanation, a caution, the consequence of an action about to be taken, or
 * the confirmation that one has been.
 *
 * Corpus: `DB:594-598` and `:600-604` (titled success and
 * caution — a 1.5px edge at 14px radius, a strong over a softer paragraph),
 * `DB:1304-1307` (the titled emergency band, a 2px `#b3261e`
 * edge on `#fdf3f2`), `DB:803,832,1133,1188,1244` (five untitled
 * success one-liners at 12px radius), `DB:742,774` (info),
 * `SI:124,127` (the advocate and tradie notes — the same band
 * twice, no border at all), `DB:302-303` (the muted workspace
 * language note, capped at 78ch and carrying `dir="auto"`). Thirteen bands
 * across two files and one recipe, because the corpus is already writing this
 * component thirteen times.
 *
 * The two-colour convention holds throughout and is why `tone` is not
 * decoration: green confirms something has happened, slate-blue explains
 * something standing, amber warns of something about to happen automatically,
 * red is an emergency, and the two greys carry a note that is none of those.
 * Colour only ever reinforces — every band is a sentence.
 *
 * Values the corpus shows that `tokens.css` has no token for, taken to the
 * nearest token rather than inlined: the whole info tone (`#eef1f6` / `#d6deea` / `#33415c`); the
 * `#cfe6da` success edge and its `#3c463f` body ink; the `#e6c9a3` caution edge
 * with `#7a4510` over `#4a4034`; `#3a3a3c` critical body ink; the 1px and 2px
 * border weights, both drawn at the one width `--rp-border-input-width` has;
 * the 9px muted radius; the 6px and 10px stack gaps; and the paddings 9px 12px,
 * 9px 13px, 11px 14px and 13px 15px. `callout.css` marks each at the line that
 * takes it.
 *
 * Two copy-rule breaches in the corpus are not reproduced. `DB`
 * assembles line 1188 from three bindings joined by a literal ` · ` and
 * prefixes a `✓` inside the bold span with nothing hiding it, so the tick is
 * announced as a character and the separator reorders against Arabic; here the
 * caller passes one node and puts the emphasis inside it. The band at
 * `SI:53-59` is a Callout with a pill in it — a molecule, and
 * not this atom, though its `prUp` entrance is kept as `entering`.
 */
export interface CalloutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  readonly tone: 'success' | 'info' | 'caution' | 'critical' | 'neutral' | 'muted';
  /** Copy. A strong, never a heading: it has no place in the document outline. */
  readonly title?: string;
  /**
   * Copy. `ReactNode` so a sentence can carry an emphasised run inside it, and
   * optional because a titled band with no body is a form the corpus draws and
   * this component renders — requiring it would have made that state reachable
   * only by passing an empty string, which is a workaround and not an API.
   * `EmptyState` reaches the same shape the same way.
   */
  readonly children?: ReactNode;
  readonly live?: 'off' | 'polite' | 'assertive';
  readonly density?: 'comfortable' | 'compact';
  readonly entering?: boolean;
}

/**
 * `live` decides the role, and the corpus is half right about which bands need
 * one. Standing context — the liability note (1244), the privacy explanation
 * (774) — is a plain container: an alert that fires on page load is noise.
 * `assertive` is `role="alert"`, which is what the auto-escalation band (601)
 * and the emergency panel (1305) are. `polite` is `role="status"`, which is
 * what the referral confirmation (803) and the verified banner (1188) needed
 * and did not have — both appear in answer to a button press, so without one
 * the outcome of the press is silent. Both roles are fixed at mount, so `live`
 * must be stable for the life of an instance.
 */
export function Callout({
  tone,
  title,
  children,
  live = 'off',
  density = 'comfortable',
  entering = false,
  className,
  dir,
  role,
  ...rest
}: CalloutProps) {
  const heading = title === undefined || title.trim() === '' ? undefined : title;
  // A band with a tint and no words is the state rule 5 of the library contract
  // forbids: colour carrying the whole meaning, and silence for anyone not
  // reading it. The component cannot supply a fallback word — a word in here is
  // copy, and copy comes from the caller's catalogue — so an empty
  // one degrades to nothing at all, as StatusChip's blank label does.
  //
  // The empty array is here because `{items.map(…)}` over an empty list is the
  // ordinary way a caller arrives at this state, and it is recognisable. What
  // is not: an element that happens to render nothing, and an array whose
  // members are each blank. Both are the caller's to not pass, and the limit is
  // stated rather than guessed at.
  const blank =
    children === undefined ||
    children === null ||
    typeof children === 'boolean' ||
    (Array.isArray(children) && children.length === 0) ||
    (typeof children === 'string' && children.trim() === '');
  if (heading === undefined && blank) return null;

  const classes = [
    'rp-callout',
    `rp-callout--${tone}`,
    `rp-callout--${density}`,
    heading === undefined ? undefined : 'rp-callout--titled',
    entering ? 'rp-callout--entering' : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      {...rest}
      role={live === 'assertive' ? 'alert' : live === 'polite' ? 'status' : role}
      // `dir="auto"` is the corpus's own at DB:303, and it is the default here
      // rather than an option: a band whose copy is translated while the page
      // around it is not renders backwards without it, which is the ordinary
      // case in a workspace shared by a tenant and a property manager.
      dir={dir ?? 'auto'}
      className={classes}
    >
      {heading === undefined ? null : <strong className="rp-callout__title">{heading}</strong>}
      {blank ? null : <p className="rp-callout__body">{children}</p>}
    </div>
  );
}
